import logger from '../utils/logger';

export const ConflictStrategy = {
  SERVER_WINS: 'server-wins',
  CLIENT_WINS: 'client-wins',
  MERGE_FIELDS: 'merge-fields',
  MANUAL: 'manual',
};

export const ConflictType = {
  NONE: 'none',
  VERSION_MISMATCH: 'version_mismatch',
  LOCAL_NEWER: 'local_newer',
  SERVER_NEWER: 'server_newer',
};

class ConflictResolver {
  constructor() {
    this._pendingConflicts = [];
    this._listeners = new Set();
    this._defaultStrategy = ConflictStrategy.SERVER_WINS;
  }

  get pendingConflicts() {
    return [...this._pendingConflicts];
  }

  get hasConflicts() {
    return this._pendingConflicts.length > 0;
  }

  setDefaultStrategy(strategy) {
    if (Object.values(ConflictStrategy).includes(strategy)) {
      this._defaultStrategy = strategy;
    }
  }

  onConflict(listener) {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  detectConflict(localRecord, serverData) {
    if (!localRecord || !serverData) return { hasConflict: false, type: ConflictType.NONE };

    const localVersion = localRecord.version || 0;
    const serverVersion = serverData.version || 0;

    if (localVersion !== serverVersion) {
      return {
        hasConflict: true,
        type: ConflictType.VERSION_MISMATCH,
        localVersion,
        serverVersion,
        localRecord,
        serverData,
      };
    }

    return { hasConflict: false, type: ConflictType.NONE };
  }

  async resolve(conflict, strategy) {
    const resolvedStrategy = strategy || this._defaultStrategy;
    let resolvedData;

    switch (resolvedStrategy) {
      case ConflictStrategy.CLIENT_WINS:
        resolvedData = { ...conflict.localRecord.data, version: conflict.serverVersion + 1 };
        break;

      case ConflictStrategy.SERVER_WINS:
        resolvedData = { ...conflict.serverData };
        break;

      case ConflictStrategy.MERGE_FIELDS:
        resolvedData = this._mergeFields(conflict.localRecord.data, conflict.serverData);
        break;

      case ConflictStrategy.MANUAL:
      default:
        this._addConflict(conflict);
        return null;
    }

    return resolvedData;
  }

  _mergeFields(localData, serverData) {
    const merged = { ...serverData };
    for (const key of Object.keys(localData)) {
      if (key === '_id' || key === 'id' || key === 'version' || key === 'updatedAt' || key === 'createdAt' || key === 'owner') continue;
      if (!(key in serverData) || serverData[key] === undefined || serverData[key] === null) {
        merged[key] = localData[key];
      }
    }
    merged.version = (serverData.version || 0) + 1;
    merged.updatedAt = new Date().toISOString();
    merged._conflictResolved = 'merged';
    return merged;
  }

  _addConflict(conflict) {
    const exists = this._pendingConflicts.some(
      c => c.localRecord?.localId === conflict.localRecord?.localId
    );
    if (!exists) {
      this._pendingConflicts.push(conflict);
      for (const listener of this._listeners) {
        try { listener(this._pendingConflicts); } catch {}
      }
    }
  }

  resolveManually(localId, resolution) {
    const idx = this._pendingConflicts.findIndex(
      c => c.localRecord?.localId === localId
    );
    if (idx !== -1) {
      const [conflict] = this._pendingConflicts.splice(idx, 1);
      for (const listener of this._listeners) {
        try { listener(this._pendingConflicts); } catch {}
      }
      return { ...conflict, resolvedData: resolution };
    }
    return null;
  }

  dismissConflict(localId) {
    const idx = this._pendingConflicts.findIndex(
      c => c.localRecord?.localId === localId
    );
    if (idx !== -1) {
      this._pendingConflicts.splice(idx, 1);
      for (const listener of this._listeners) {
        try { listener(this._pendingConflicts); } catch {}
      }
    }
  }

  clearAll() {
    this._pendingConflicts = [];
    for (const listener of this._listeners) {
      try { listener([]); } catch {}
    }
  }
}

const conflictResolver = new ConflictResolver();
export default conflictResolver;
